import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Alert, Box, Grid, Typography } from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SectionHeader from '../../../components/shared/SectionHeader';
import TextField from '../../../components/form/TextField';
import PhoneField from '../../../components/form/PhoneField';
import NumberField from '../../../components/form/NumberField';
import CurrencyField from '../../../components/form/CurrencyField';
import CheckboxField from '../../../components/form/CheckboxField';
import DatePickerField from '../../../components/form/DatePickerField';
import { fmtDollar } from '../../../utils/formatters';

const num = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : 0);

const EmploymentIncomeStep = () => {
  const { setValue } = useFormContext();
  const [baseIncome, overtime, bonus, commission, otherIncome, selfEmployed] = useWatch({
    name: [
      'employment.baseIncome',
      'employment.overtime',
      'employment.bonus',
      'employment.commission',
      'employment.otherIncome',
      'employment.selfEmployed',
    ],
  });

  const total =
    num(baseIncome) + num(overtime) + num(bonus) + num(commission) + num(otherIncome);

  useEffect(() => {
    setValue('employment.totalMonthlyIncome', total, { shouldDirty: false });
  }, [total, setValue]);

  return (
    <Box>
      <SectionHeader title="Employment & Income" icon={<WorkIcon />} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Current employment information and monthly income
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            name="employment.employerName"
            label={selfEmployed ? 'Business Name' : 'Employer Name'}
            required
            size="small"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField name="employment.position" label="Position / Title" required size="small" />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <PhoneField name="employment.employerPhone" label="Employer Phone" size="small" />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <NumberField name="employment.yearsEmployed" label="Years Employed" fullWidth size="small" step={0.5} />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <DatePickerField name="employment.employmentStartDate" label="Start Date" />
        </Grid>
        <Grid size={12}>
          <CheckboxField name="employment.selfEmployed" label="I am self-employed" />
        </Grid>
      </Grid>

      {selfEmployed && (
        <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
          Self-employed borrowers may be asked for tax returns or a year-to-date profit and loss statement.
        </Alert>
      )}

      <Box sx={{ mt: 3 }}>
        <SectionHeader title="Monthly Income" icon={<AttachMoneyIcon />} />
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <CurrencyField name="employment.baseIncome" label="Base Monthly Income" size="small" />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <CurrencyField name="employment.overtime" label="Overtime" size="small" />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <CurrencyField name="employment.bonus" label="Bonus" size="small" />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <CurrencyField name="employment.commission" label="Commission" size="small" />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <CurrencyField name="employment.otherIncome" label="Other Income" size="small" />
          </Grid>
        </Grid>

        <Alert
          severity="info"
          icon={<AttachMoneyIcon fontSize="inherit" />}
          sx={{ mt: 2 }}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            Total Monthly Income
          </Typography>
          <Typography variant="h4" component="p">
            {fmtDollar(total)}
          </Typography>
        </Alert>
      </Box>
    </Box>
  );
};

export default EmploymentIncomeStep;
